import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import {
  Box, Button, CircularProgress, Stack, Tooltip, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import ImageIcon from "@mui/icons-material/Image";
import { API_BASE_URL } from "../config";
import { notify } from "../notify";

export const GUIDELINE_PHOTOS_API = `${API_BASE_URL}/guidelines/photos`;

// rehype-raw lets the raw HTML in the text through (the toolbar's underline is
// <u>…</u>), which without a sanitiser would make any rich-text field a stored
// cross-site scripting hole — and incident notes can be written by everyone on an
// event. So the raw pass is followed by a sanitising one, over GitHub's default
// schema plus the one tag the editor itself emits.
const SANITIZE_SCHEMA = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), "u"],
};

// Photos embedded in rich text are stored as Markdown image references pointing
// at "/guidelines/photos/{id}" or "/incidents/photos/{id}". Those need the bearer
// token, so a plain <img> would 401 — fetch the blob via axios (token attached by
// the interceptor) and render it from an object URL instead. Mirrors
// components/DocumentLink.jsx.
function AuthedImage({ src, alt }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!src) return undefined;
    let active = true;
    let objectUrl = null;
    const full = src.startsWith("http") ? src : `${API_BASE_URL}${src}`;
    axios.get(full, { responseType: "blob", suppressErrorToast: true })
      .then(res => {
        if (!active) return;
        objectUrl = URL.createObjectURL(res.data);
        setUrl(objectUrl);
      })
      .catch(() => { if (active) setFailed(true); });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  if (failed) return <Box component="span" sx={{ color: "error.main" }}>[image unavailable]</Box>;
  if (!url) return <CircularProgress size={18} sx={{ my: 1 }} />;
  // onError as well as the fetch failing: the bytes can arrive fine and still be
  // something this browser can't decode (a HEIC straight off a phone), and a
  // silently broken image would read as though there was no photo.
  return <Box component="img" src={url} alt={alt || ""} onError={() => setFailed(true)}
              sx={{ maxWidth: "100%", height: "auto", borderRadius: 1, my: 1 }} />;
}

const MD_COMPONENTS = {
  img: ({ src, alt }) => <AuthedImage src={src} alt={alt} />,
};

/**
 * Read-only render of Markdown rich text (bold/italic/underline/lists/photos).
 * `dense` matches the surrounding body2 type — for a note or a collapsed section
 * rather than a guideline page.
 *
 * remark-breaks is deliberate: most of this text was typed as plain prose (and
 * incidents hold years of it, written before the field was rich text at all), so a
 * single newline has to stay a line break rather than being folded into the
 * paragraph as Markdown would normally have it.
 */
export function RichTextView({ value, dense = false }) {
  if (!value || !value.trim()) return null;
  return (
    <Box sx={{
      "& img": { display: "block" },
      "& p": { my: dense ? 0.5 : 1 },
      "& p:first-of-type": { mt: 0 },
      "& p:last-of-type": { mb: 0 },
      "& ul, & ol": { pl: 3, my: dense ? 0.5 : 1 },
      "& h1, & h2, & h3": { mt: 2, mb: 1 },
      ...(dense ? { fontSize: "0.875rem", lineHeight: 1.43 } : {}),
      wordBreak: "break-word",
    }}>
      <Markdown remarkPlugins={[remarkGfm, remarkBreaks]}
                rehypePlugins={[rehypeRaw, [rehypeSanitize, SANITIZE_SCHEMA]]}
                components={MD_COMPONENTS}>
        {value}
      </Markdown>
    </Box>
  );
}

// Photos are downscaled in the browser before upload. An event runs on a phone on
// a hillside: a 12 MP camera original costs minutes to send and is then fetched
// again by everyone who opens the incident, for detail no reader needs.
const MAX_PHOTO_DIM = 1600;      // longest edge, in pixels
const RECODE_OVER_BYTES = 1024 * 1024;  // re-encode anything bigger, even if small in pixels
const JPEG_QUALITY = 0.85;

/**
 * A downscaled JPEG copy of an image file, or the file itself when it's already
 * small enough (or can't be decoded — better to send the original than nothing).
 */
async function downscale(file) {
  if (!file.type.startsWith("image/")) return file;
  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }
  const scale = Math.min(1, MAX_PHOTO_DIM / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && file.size <= RECODE_OVER_BYTES) { bitmap.close?.(); return file; }
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  const blob = await new Promise(res => canvas.toBlob(res, "image/jpeg", JPEG_QUALITY));
  if (!blob || blob.size >= file.size) return file;   // no gain — keep the original
  const name = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
  return new File([blob], name, { type: "image/jpeg" });
}

/**
 * Markdown editor with a formatting toolbar and an Edit/Preview toggle.
 *
 * Photos can be added three ways — the toolbar button, pasting from the clipboard,
 * or dropping a file on the box — and are uploaded to `uploadUrl`, which returns
 * `{ url }` for the reference inserted into the text. With no `uploadUrl` the
 * photo controls are disabled and `uploadHint` says why (an incident has to be
 * saved before a photo can be attached to it).
 *
 * `photos={false}` drops the photo button altogether, for text that never carries
 * images (an event's information): a pasted or dropped image is then ignored
 * rather than explained.
 *
 * Props: value (string), onChange (str => void), minRows (number),
 * disabled (bool), uploadUrl (string|null), uploadHint (string), photos (bool).
 */
export function RichTextEditor({
  value, onChange, minRows = 4, disabled = false,
  uploadUrl = null, uploadHint = "Photos can't be added here.", photos = true,
}) {
  const ref = useRef(null);
  const [mode, setMode] = useState("edit"); // "edit" | "preview"
  const [uploading, setUploading] = useState(false);
  // The text as it stands *including* edits made in this tick. Several photos can
  // land back to back (one paste of two files), and each insert has to build on the
  // last rather than on the value React last rendered with.
  const valueRef = useRef(value);
  valueRef.current = value;

  // Replace the current selection with `before + selectedText + after`, keeping
  // the selection wrapped so the user can keep typing / toggle again.
  const wrap = (before, after = before) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const text = value || "";
    const selected = text.slice(start, end);
    const next = text.slice(0, start) + before + selected + after + text.slice(end);
    onChange(next);
    // Restore a sensible selection after React re-renders the textarea.
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = start + before.length;
      el.selectionEnd = end + before.length;
    });
  };

  // Prefix each line of the selection (or current line) with `marker`.
  const prefixLines = (markerFor) => {
    const el = ref.current;
    if (!el) return;
    const text = value || "";
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const lineStart = text.lastIndexOf("\n", start - 1) + 1;
    const block = text.slice(lineStart, end);
    const lines = block.split("\n");
    const rebuilt = lines.map((ln, i) => `${markerFor(i)}${ln}`).join("\n");
    const next = text.slice(0, lineStart) + rebuilt + text.slice(end);
    onChange(next);
    requestAnimationFrame(() => { el.focus(); });
  };

  const insertAtCursor = (snippet) => {
    const el = ref.current;
    const text = valueRef.current || "";
    const start = Math.min(el ? el.selectionStart : text.length, text.length);
    const next = text.slice(0, start) + snippet + text.slice(start);
    valueRef.current = next;
    onChange(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + snippet.length;
      el.selectionStart = el.selectionEnd = pos;
    });
  };

  // Upload each image and insert a Markdown reference to it, in the order given.
  // Sequentially, so two photos pasted at once keep that order in the text.
  const uploadPhotos = async (files) => {
    const images = [...files].filter(f => f.type && f.type.startsWith("image/"));
    if (!images.length) return;
    if (!uploadUrl) { notify(uploadHint, "info"); return; }
    setUploading(true);
    try {
      for (const original of images) {
        const file = await downscale(original);
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(uploadUrl, fd);
        insertAtCursor(`\n\n![${file.name}](${res.data.url})\n\n`);
      }
    } catch {
      /* the axios interceptor has already shown the failure as a toast */
    } finally {
      setUploading(false);
    }
  };

  // Pasting a screenshot or a photo is the point of this: the clipboard carries the
  // image as a file, and pasting it as text would otherwise do nothing at all.
  const handlePaste = (e) => {
    if (!photos) return;
    const files = e.clipboardData?.files;
    if (!files?.length) return;
    if (![...files].some(f => f.type && f.type.startsWith("image/"))) return;
    e.preventDefault();
    uploadPhotos(files);
  };

  const handleDrop = (e) => {
    const files = e.dataTransfer?.files;
    if (!files?.length) return;
    e.preventDefault();   // a dropped file would otherwise navigate away from the app
    if (photos) uploadPhotos(files);
  };

  const btn = (title, icon, onClick) => (
    <Tooltip title={title}>
      <span>
        <Button size="small" onClick={onClick} disabled={disabled || mode !== "edit"}
                sx={{ minWidth: 40, px: 1 }}>{icon}</Button>
      </span>
    </Tooltip>
  );

  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
      <Stack direction="row" spacing={0.5}
             sx={{ alignItems: "center", p: 0.5, borderBottom: "1px solid", borderColor: "divider", flexWrap: "wrap" }}>
        {btn("Bold", <FormatBoldIcon fontSize="small" />, () => wrap("**"))}
        {btn("Italic", <FormatItalicIcon fontSize="small" />, () => wrap("*"))}
        {btn("Underline", <FormatUnderlinedIcon fontSize="small" />, () => wrap("<u>", "</u>"))}
        {btn("Bulleted list", <FormatListBulletedIcon fontSize="small" />, () => prefixLines(() => "- "))}
        {btn("Numbered list", <FormatListNumberedIcon fontSize="small" />, () => prefixLines(i => `${i + 1}. `))}
        {photos && (
        <Tooltip title={uploadUrl ? "Insert photo — or paste one from the clipboard" : uploadHint}>
          <span>
            <Button size="small" component="label"
                    disabled={disabled || mode !== "edit" || uploading || !uploadUrl}
                    sx={{ minWidth: 40, px: 1 }}>
              {uploading ? <CircularProgress size={16} /> : <ImageIcon fontSize="small" />}
              <input hidden type="file" accept="image/*" multiple
                     onChange={e => { uploadPhotos(e.target.files); e.target.value = ""; }} />
            </Button>
          </span>
        </Tooltip>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <ToggleButtonGroup size="small" exclusive value={mode}
                           onChange={(_, m) => m && setMode(m)}>
          <ToggleButton value="edit" sx={{ py: 0.25 }}>Edit</ToggleButton>
          <ToggleButton value="preview" sx={{ py: 0.25 }}>Preview</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {mode === "edit" ? (
        <Box
          component="textarea"
          ref={ref}
          value={value || ""}
          disabled={disabled}
          onChange={e => onChange(e.target.value)}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          rows={minRows}
          spellCheck
          sx={{
            width: "100%", border: "none", outline: "none", resize: "vertical",
            p: 1.5, fontFamily: "inherit", fontSize: "0.95rem", lineHeight: 1.5,
            bgcolor: "transparent", color: "text.primary", boxSizing: "border-box",
          }}
        />
      ) : (
        <Box sx={{ p: 1.5, minHeight: minRows * 24 }}>
          {value && value.trim()
            ? <RichTextView value={value} />
            : <Box sx={{ color: "text.secondary", fontStyle: "italic" }}>Nothing to preview</Box>}
        </Box>
      )}
    </Box>
  );
}
